import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { setSaleStatusesCache, saleStatusCls } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircleQuestion, Plus, Pencil, Trash2, Check, X, CheckCircle2, XCircle, FileText, RotateCcw, Plug, UserCheck, Network, Factory, ArrowUp, ArrowDown, ListChecks, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import { ContactsManager } from "@/pages/AssignerSettingsPage";

const HubsCard = () => {
  const [hubs, setHubs] = useState([]);
  const [name, setName] = useState("");
  const [zone, setZone] = useState("Green Zone");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
  }, []);

  const persist = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/settings/hubs", { hubs: next });
      setHubs(r.data.hubs);
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setBusy(false); }
  };

  const add = async () => {
    const t = name.trim();
    if (!t) return;
    if (hubs.some((h) => h.name.toLocaleLowerCase("tr") === t.toLocaleLowerCase("tr"))) return toast.error("Bu hub zaten kayıtlı");
    if (await persist([...hubs, { name: t, zone }])) { setName(""); toast.success("Hub eklendi"); }
  };

  const remove = async (h) => {
    if (await persist(hubs.filter((x) => x.name !== h.name))) toast.success("Hub silindi");
  };

  const changeZone = async (h, z) => {
    if (await persist(hubs.map((x) => (x.name === h.name ? { ...x, zone: z } : x)))) toast.success(`${h.name} → ${z === "all" ? "Tüm Bölgeler" : z}`);
  };

  const zoneLabel = (z) => (z === "all" ? "Tüm Bölgeler" : z);

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="hubs-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Network size={18} className="text-blue-600" /> Hub'lar
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Her hub bir bölgeye bağlıdır; hub seçenekleri yalnızca bağlı olduğu bölgenin projelerinde ve özet sekmesinde görünür.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 flex gap-2 flex-wrap">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Yeni hub adı" data-testid="new-hub-input" className="rounded-none border-slate-200 h-11 flex-1 min-w-[180px]" />
        <Select value={zone} onValueChange={setZone}>
          <SelectTrigger className="rounded-none border-slate-200 h-11 w-[160px]" data-testid="new-hub-zone-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200 rounded-none">
            <SelectItem value="Green Zone">Green Zone</SelectItem>
            <SelectItem value="Blue Zone">Blue Zone</SelectItem>
            <SelectItem value="Ofisler">Ofisler</SelectItem>
            <SelectItem value="all">Tüm Bölgeler</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={add} disabled={busy || !name.trim()} data-testid="add-hub-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Plus size={15} /> Ekle
        </Button>
      </div>
      <div className="px-6 py-4">
        {hubs.length === 0 ? (
          <p className="text-sm text-slate-400">Henüz hub yok.</p>
        ) : (
          <ul className="space-y-1.5">
            {hubs.map((h) => (
              <li key={h.name} className="flex items-center gap-3 border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm" data-testid={`hub-item-${h.name}`}>
                <span className="font-semibold">{h.name}</span>
                <Select value={h.zone || "all"} onValueChange={(z) => changeZone(h, z)}>
                  <SelectTrigger className={`rounded-none h-7 w-[140px] text-xs ml-auto ${h.zone === "Green Zone" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : h.zone === "Blue Zone" ? "border-blue-300 text-blue-700 bg-blue-50" : "border-slate-300 text-slate-600 bg-white"}`} data-testid={`hub-zone-${h.name}`}>
                    <SelectValue>{zoneLabel(h.zone || "all")}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    <SelectItem value="Green Zone">Green Zone</SelectItem>
                    <SelectItem value="Blue Zone">Blue Zone</SelectItem>
                    <SelectItem value="Ofisler">Ofisler</SelectItem>
                    <SelectItem value="all">Tüm Bölgeler</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => remove(h)} disabled={busy} data-testid={`delete-hub-${h.name}`} className="text-slate-400 hover:text-red-600 transition-colors duration-150" aria-label="Sil">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const SaleStatusesCard = () => {
  const [list, setList] = useState([]);
  const [newName, setNewName] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/sale-statuses").then((r) => setList(r.data.statuses)).catch(() => {});
  }, []);

  const persist = async (next, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/sale-statuses", { statuses: next });
      setList(data.statuses);
      setSaleStatusesCache(data.statuses);
      toast.success(msg);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const add = () => {
    const v = newName.trim();
    if (!v) return;
    if (list.includes(v)) return toast.error("Bu adda bir durum zaten var");
    persist([...list, v], "Durum eklendi");
    setNewName("");
  };
  const remove = (s) => {
    if (!window.confirm(`"${s}" durumu listeden kaldırılsın mı?\nBu duruma sahip projelerdeki değer korunur ancak seçeneklerde görünmez.`)) return;
    persist(list.filter((x) => x !== s), "Durum kaldırıldı");
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next, "Sıralama güncellendi");
  };
  const rename = async (oldName) => {
    const v = editVal.trim();
    setEditIdx(null);
    if (!v || v === oldName) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/sale-statuses/rename", { old: oldName, new: v });
      setList(data.statuses);
      setSaleStatusesCache(data.statuses);
      toast.success(`Durum yeniden adlandırıldı — ${data.updated_projects} proje güncellendi`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="sale-statuses-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <ListChecks size={18} className="text-blue-600" /> Durum Seçenekleri
        </div>
        <p className="text-xs text-slate-500 mt-1">Projelerdeki "Durum" dropdown'unda görünen satış süreci adımları. Ekleyin, yeniden adlandırın, kaldırın veya oklarla sırasını değiştirin — değişiklik tüm sayfalara anında yansır.</p>
      </div>
      <div className="px-6 py-4 divide-y divide-slate-100">
        {list.map((s, i) => (
          <div key={s} className="flex items-center gap-2 py-2" data-testid={`sale-status-row-${i}`}>
            <span className="text-xs text-slate-300 tabular-nums w-5">{i + 1}</span>
            {editIdx === i ? (
              <Input
                autoFocus
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                onBlur={() => rename(s)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditIdx(null); }}
                data-testid={`sale-status-edit-input-${i}`}
                className="rounded-none h-9 border-slate-300 flex-1"
              />
            ) : (
              <span className={`flex-1 text-sm font-semibold px-2.5 py-1 border ${saleStatusCls(s)}`}>{s}</span>
            )}
            <button type="button" disabled={busy || i === 0} onClick={() => move(i, -1)} data-testid={`sale-status-up-${i}`} title="Yukarı taşı"
              className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white hover:border-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150">
              <ArrowUp size={13} />
            </button>
            <button type="button" disabled={busy || i === list.length - 1} onClick={() => move(i, 1)} data-testid={`sale-status-down-${i}`} title="Aşağı taşı"
              className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white hover:border-slate-900 disabled:opacity-30 disabled:pointer-events-none transition-colors duration-150">
              <ArrowDown size={13} />
            </button>
            <button type="button" disabled={busy} onClick={() => { setEditIdx(i); setEditVal(s); }} data-testid={`sale-status-rename-${i}`} title="Yeniden adlandır"
              className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors duration-150">
              <Pencil size={13} />
            </button>
            <button type="button" disabled={busy} onClick={() => remove(s)} data-testid={`sale-status-delete-${i}`} title="Kaldır"
              className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors duration-150">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <div className="px-6 pb-5 pt-1 flex gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Yeni durum adı..."
          data-testid="sale-status-new-input"
          className="rounded-none h-10 border-slate-200 flex-1"
        />
        <Button onClick={add} disabled={busy || !newName.trim()} data-testid="sale-status-add-button" className="rounded-none h-10 px-4 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Plus size={14} /> Ekle
        </Button>
      </div>
    </div>
  );
};

const ManufacturerCard = () => {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/manufacturer").then((r) => setForm({ company: "", contact_name: "", email: "", phone: "", ...r.data })).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/manufacturer", form);
      setForm(r.data);
      toast.success("Üretici bilgileri kaydedildi");
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setBusy(false); }
  };

  const assignAll = async () => {
    if (!form.company.trim()) return toast.error("Önce firma adını girip kaydedin");
    setBusy(true);
    try {
      await api.put("/settings/manufacturer", form);
      const { data } = await api.post("/settings/manufacturer/assign-templates");
      toast.success(`${data.matched} şablon projeye üretici atandı`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const field = (key, label, placeholder, type = "text") => (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} data-testid={`manufacturer-${key}-input`} className="rounded-none border-slate-200 h-11" />
    </div>
  );

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="manufacturer-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Factory size={18} className="text-amber-600" /> Üretici (Şablon Standlar)
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Şablon standların üreticisi. "Tüm Şablonlara Ata" ile Stand Türü "Template" veya "Customized Template" olan tüm projelere tek tıkla atanır ve Projeler listesinde Kazanan/Tedarikçi sütununda görünür.
        </p>
      </div>
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {field("company", "Firma Adı", "örn. Quattro Stand")}
        {field("contact_name", "Yetkili", "Ad Soyad")}
        {field("email", "E-posta", "uretici@firma.com", "email")}
        {field("phone", "Telefon", "+90...")}
      </div>
      <div className="px-6 pb-5 flex gap-2 flex-wrap">
        <Button onClick={save} disabled={busy} data-testid="manufacturer-save-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Check size={15} /> Kaydet
        </Button>
        <Button onClick={assignAll} disabled={busy} variant="outline" data-testid="manufacturer-assign-all-button" className="rounded-none h-11 px-5 border-amber-400 text-amber-700 hover:bg-amber-50 gap-2 font-semibold transition-colors duration-200">
          <Factory size={15} /> Tüm Şablonlara Ata
        </Button>
      </div>
    </div>
  );
};

const ApproversCard = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/approvers").then((r) => { setItems(r.data.approvers || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const persist = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/settings/approvers", { approvers: next });
      setItems(r.data.approvers);
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setBusy(false); }
  };

  const add = async () => {
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return toast.error("Geçerli bir e-posta girin");
    if (items.some((i) => i.email === em)) return toast.error("Bu e-posta zaten kayıtlı");
    if (await persist([...items, { name: name.trim(), email: em }])) {
      setName(""); setEmail("");
      toast.success("Onaylayıcı eklendi");
    }
  };

  const remove = async (id) => {
    if (await persist(items.filter((i) => i.id !== id))) toast.success("Onaylayıcı silindi");
  };

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="approvers-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <UserCheck size={18} className="text-emerald-600" /> Onaylayıcılar
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Teklif Onayı sayfasındaki "Onaylayacak Kişi" listesinde en üstte görünen kayıtlı kişiler. Her onayda tekrar e-posta yazmanıza gerek kalmaz.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad (opsiyonel)" data-testid="new-approver-name" className="rounded-none border-slate-200 h-11 sm:max-w-[220px]" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="kisi@firma.com" data-testid="new-approver-email" className="rounded-none border-slate-200 h-11 flex-1" />
        <Button onClick={add} disabled={busy || !email.trim()} data-testid="add-approver-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Plus size={15} /> Ekle
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-500 text-sm" data-testid="approvers-empty">
          {loaded ? "Henüz kayıtlı onaylayıcı yok." : "Yükleniyor..."}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((a) => (
            <li key={a.id} className="px-6 py-3 flex items-center gap-3" data-testid={`approver-row-${a.id}`}>
              <span className="text-sm font-medium">{a.name || "—"}</span>
              <span className="text-sm text-slate-500 flex-1">{a.email}</span>
              <button onClick={() => remove(a.id)} disabled={busy} data-testid={`delete-approver-${a.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const IntegrationCard = () => {
  const [cfg, setCfg] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/integration/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/integration/config", cfg); toast.success("Entegrasyon ayarları kaydedildi"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const regen = async () => {
    setBusy(true);
    try { const r = await api.post("/integration/regenerate-key"); set("api_key", r.data.api_key); toast.success("Yeni API anahtarı üretildi — eski anahtar geçersiz"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const testHook = async () => {
    setBusy(true);
    try { const r = await api.post("/integration/test-webhook"); toast.success(`Test gönderildi — yanıt kodu: ${r.data.status_code}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="integration-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Plug size={18} className="text-slate-600" /> Dış Entegrasyon (API & Webhook)
        </div>
        <p className="text-xs text-slate-500 mt-1">Başka uygulamalarınız GettProposal verilerini bu API ile çekebilir; olaylar webhook ile anlık iletilir.</p>
      </div>
      <div className="px-6 py-5 space-y-5">
        <div className="space-y-1.5">
          <Label>API Anahtarı (istekte <code className="bg-slate-100 px-1">X-API-Key</code> başlığı)</Label>
          <div className="flex gap-2">
            <Input readOnly value={showKey ? cfg.api_key : "•".repeat(24)} data-testid="integration-api-key" className="rounded-none bg-slate-50 border-slate-200 font-mono text-xs h-10" />
            <Button variant="outline" onClick={() => setShowKey((v) => !v)} data-testid="toggle-api-key" className="rounded-none h-10 border-slate-200">{showKey ? "Gizle" : "Göster"}</Button>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(cfg.api_key); toast.success("Kopyalandı"); }} data-testid="copy-api-key" className="rounded-none h-10 border-slate-200">Kopyala</Button>
            <Button variant="outline" onClick={regen} disabled={busy} data-testid="regen-api-key" className="rounded-none h-10 border-red-200 text-red-600 hover:bg-red-50">Yenile</Button>
          </div>
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-3 space-y-1">
          <div className="font-semibold text-slate-700">Veri çekme uç noktaları (GET, hepsi `?updated_since=ISO` destekler):</div>
          <div className="font-mono">/api/external/tenders · /api/external/offers · /api/external/projects</div>
          <div className="font-mono">/api/external/suppliers · /api/external/customers · /api/external/leads · /api/external/supplier-applications</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Webhook URL</Label>
            <Input value={cfg.webhook_url} onChange={(e) => set("webhook_url", e.target.value)} placeholder="https://uygulamam.com/webhook" data-testid="webhook-url-input" className="rounded-none bg-white border-slate-200 h-10" />
          </div>
          <div className="space-y-1.5">
            <Label>Webhook Secret (X-Webhook-Secret başlığıyla gönderilir)</Label>
            <Input value={cfg.webhook_secret} onChange={(e) => set("webhook_secret", e.target.value)} data-testid="webhook-secret-input" className="rounded-none bg-white border-slate-200 h-10" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={cfg.webhook_enabled} onChange={(e) => set("webhook_enabled", e.target.checked)} data-testid="webhook-enabled-checkbox" />
          Webhook aktif (olaylar: offer.created, offer.accepted, proposal.countered, test)
        </label>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} data-testid="save-integration-button" className="rounded-none h-11 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold">Kaydet</Button>
          <Button variant="outline" onClick={testHook} disabled={busy || !cfg.webhook_url} data-testid="test-webhook-button" className="rounded-none h-11 border-slate-200">Test Gönder</Button>
        </div>
      </div>
    </div>
  );
};

const SAMPLE_VALUES = {
  firma: "Örnek Tedarikçi AŞ",
  ihale: "Örnek Fuar Standı İhalesi",
  tutar: "1.250.000,00",
  para_birimi: "TRY",
  yetkili: "Ahmet Yılmaz",
  teklif_sayisi: "3",
};

const TemplateCard = ({ endpoint = "/settings/request-template", title = "Talep Metni Şablonu", desc = "Teklif Al formunda varsayılan olarak gelen metin. Talep oluştururken yine de düzenlenebilir.", tid = "template", placeholders = null }) => {
  const [text, setText] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(endpoint).then((r) => { setText(r.data.text); setIsDefault(r.data.is_default); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!text.trim()) return toast.error("Şablon metni boş olamaz");
    setBusy(true);
    try {
      await api.put(endpoint, { text });
      setIsDefault(false);
      toast.success(`${title} kaydedildi`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.delete(endpoint);
      setText(r.data.text);
      setIsDefault(true);
      toast.success("Şablon varsayılana döndürüldü");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid={`${tid}-card`}>
      <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-3">
        <div>
          <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
            <FileText size={18} className="text-slate-600" /> {title}
          </div>
          <p className="text-xs text-slate-500 mt-1">{desc}</p>
        </div>
        {!isDefault && (
          <button onClick={reset} disabled={busy} data-testid={`reset-${tid}-button`} title="Varsayılana dön" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 shrink-0 transition-colors duration-200">
            <RotateCcw size={12} /> Varsayılana Dön
          </button>
        )}
      </div>
      <div className="px-6 py-5 space-y-4">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} data-testid={`${tid}-textarea`} className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm leading-relaxed" />
        {placeholders && (
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500">
            <span>Yer tutucular:</span>
            {placeholders.map((p) => (
              <code key={p} className="bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-700">{`{${p}}`}</code>
            ))}
            <span className="text-slate-400">— gönderim sırasında gerçek değerlerle doldurulur.</span>
          </div>
        )}
        {placeholders && text.trim() && (
          <div className="border border-dashed border-slate-300 bg-slate-50 p-4" data-testid={`${tid}-preview`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Canlı Önizleme — örnek verilerle</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {placeholders.reduce((t, p) => t.replaceAll(`{${p}}`, SAMPLE_VALUES[p] || `{${p}}`), text)}
            </div>
          </div>
        )}
        <Button onClick={save} disabled={busy || !text.trim()} data-testid={`save-${tid}-button`} className="rounded-none h-11 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
          {busy ? "Kaydediliyor..." : "Şablonu Kaydet"}
        </Button>
      </div>
    </div>
  );
};

const ServiceSection = ({ kind, title, color, icon: Icon }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newT, setNewT] = useState("");
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const load = () => api.get("/service-items").then((r) => { setItems(r.data.filter((x) => x.kind === kind)); setLoaded(true); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newT.trim()) return;
    try {
      await api.post("/service-items", { text: newT, kind });
      setNewT("");
      toast.success("Hizmet eklendi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async (it) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/service-items/${it.id}`, { text: editText, kind });
      setEditing(null);
      toast.success("Hizmet güncellendi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (it) => {
    try {
      await api.delete(`/service-items/${it.id}`);
      toast.success("Hizmet silindi");
      setItems((xs) => xs.filter((x) => x.id !== it.id));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex-1 min-w-[280px]" data-testid={`service-section-${kind}`}>
      <div className={`text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5 ${color}`}>
        <Icon size={13} /> {title}
      </div>
      <div className="flex gap-2 mb-3">
        <Input value={newT} onChange={(e) => setNewT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Yeni hizmet yazın..." data-testid={`new-service-input-${kind}`} className="rounded-none border-slate-200 h-10" />
        <Button onClick={add} disabled={!newT.trim()} data-testid={`add-service-button-${kind}`} className="rounded-none h-10 px-4 bg-slate-900 text-white hover:bg-slate-700 transition-colors duration-200"><Plus size={14} /></Button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 py-3">{loaded ? "Henüz hizmet tanımlanmadı." : "Yükleniyor..."}</div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-100">
          {items.map((it) => (
            <li key={it.id} className="px-3 py-2.5 flex items-center gap-2.5" data-testid={`service-row-${it.id}`}>
              {editing === it.id ? (
                <>
                  <Input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(it); }} data-testid={`edit-service-input-${it.id}`} className="rounded-none border-slate-200 h-8 flex-1 text-sm" autoFocus />
                  <button onClick={() => saveEdit(it)} data-testid={`save-service-${it.id}`} title="Kaydet" className="text-emerald-600 hover:text-emerald-500 transition-colors duration-200"><Check size={15} /></button>
                  <button onClick={() => setEditing(null)} title="Vazgeç" className="text-slate-400 hover:text-slate-600 transition-colors duration-200"><X size={15} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm">{it.text}</span>
                  <button onClick={() => { setEditing(it.id); setEditText(it.text); }} data-testid={`edit-service-${it.id}`} title="Düzenle" className="text-slate-400 hover:text-slate-700 transition-colors duration-200"><Pencil size={13} /></button>
                  <button onClick={() => remove(it)} data-testid={`delete-service-${it.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={13} /></button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ProjectCombobox = ({ org, projects, tabZoneName, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const sel = projects.find((p) => p.id === org.project_id);
  const label = (p) => `${p.name}${p.stand_no ? ` — ${p.stand_no}` : ""}`;
  const s = search.toLocaleLowerCase("tr").trim();
  const match = (p) => !s || `${p.name} ${p.stand_no || ""}`.toLocaleLowerCase("tr").includes(s);
  const inZone = projects.filter((p) => p.zone === tabZoneName && match(p));
  const other = projects.filter((p) => p.zone !== tabZoneName && match(p));
  const pick = (id) => { setOpen(false); setSearch(""); onSelect(id); };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`ratecard-select-${org.organisation}`}
          className={`h-9 border px-2 text-xs bg-white w-[260px] shrink-0 flex items-center justify-between gap-1.5 text-left ${org.project_id ? "border-emerald-300" : "border-slate-200"}`}
        >
          <span className={`truncate ${sel ? "text-slate-800" : "text-slate-400"}`}>{sel ? label(sel) : "— Proje seç —"}</span>
          <ChevronDown size={13} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-0 rounded-none border-slate-200" data-testid={`ratecard-combobox-${org.organisation}`}>
        <div className="p-2 border-b border-slate-100">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Proje ara..."
            autoFocus
            data-testid={`ratecard-project-search-${org.organisation}`}
            className="rounded-none h-8 text-xs border-slate-200"
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {org.project_id && (
            <button type="button" onClick={() => pick("")} data-testid={`ratecard-unmap-${org.organisation}`} className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 font-semibold">
              Eşleştirmeyi kaldır
            </button>
          )}
          {inZone.length === 0 && other.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">Sonuç yok.</div>}
          {inZone.map((p) => (
            <button key={p.id} type="button" onClick={() => pick(p.id)} className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${p.id === org.project_id ? "bg-emerald-50 font-bold text-emerald-700" : "text-slate-700"}`}>
              {label(p)}
            </button>
          ))}
          {other.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 border-y border-slate-100">Diğer bölge</div>
              {other.map((p) => (
                <button key={p.id} type="button" onClick={() => pick(p.id)} className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${p.id === org.project_id ? "bg-emerald-50 font-bold text-emerald-700" : "text-slate-700"}`}>
                  {label(p)} <span className="text-slate-400">({p.zone})</span>
                </button>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const RatecardMapCard = () => {
  const [orgs, setOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [tab, setTab] = useState("blue");

  useEffect(() => {
    api.get("/ratecard/organisations").then((r) => { setOrgs(r.data); setLoaded(true); }).catch((e) => { setErr(apiError(e)); setLoaded(true); });
    api.get("/projects").then((r) => setProjects((r.data || []).map((p) => ({ id: p.id, name: p.name, stand_no: p.stand_no, zone: p.zone })).sort((a, b) => a.name.localeCompare(b.name, "tr")))).catch(() => {});
  }, []);

  const setMap = async (org, projectId) => {
    try {
      await api.post("/ratecard/map", { organisation: org.organisation, project_id: projectId || null });
      setOrgs((os) => os.map((x) => (x.organisation === org.organisation ? { ...x, project_id: projectId || null, project: projects.find((p) => p.id === projectId) || null } : x)));
      toast.success(projectId ? `"${org.organisation}" eşleştirildi` : "Eşleştirme kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };

  const inTab = (o, t) => !o.zone || (o.zone || "").toLowerCase() === t;
  const tabOrgs = orgs.filter((o) => inTab(o, tab));
  const list = tabOrgs.filter((o) => (!onlyUnmapped || !o.project_id) && o.organisation.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));
  const unmappedCount = tabOrgs.filter((o) => !o.project_id).length;
  const tabZoneName = tab === "blue" ? "Blue Zone" : "Green Zone";

  return (
    <div className="bg-white border border-slate-200 p-6" data-testid="ratecard-map-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2"><ShoppingCart size={16} /> Ratecard31 Sipariş Eşleştirme</h2>
          <p className="text-xs text-slate-400 mt-1">ratecard31.com'dan gelen firma adlarını portaldaki projelerle eşleştirin. Eşleşen siparişler, projeye atanmış herkesin Siparişler sayfasında görünür.</p>
        </div>
        <Link to="/siparisler" data-testid="ratecard-orders-link" className="inline-flex items-center gap-1.5 text-xs font-bold border border-slate-300 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
          <ShoppingCart size={13} /> Siparişleri Gör
        </Link>
      </div>
      {!loaded && <div className="py-8 text-center text-slate-400 text-sm">Ratecard31'den firmalar yükleniyor...</div>}
      {err && <div className="mt-4 text-xs font-semibold bg-red-50 text-red-700 border border-red-200 px-3 py-2">{err}</div>}
      {loaded && !err && (
        <>
          <div className="mt-4 flex border border-slate-200 w-fit" data-testid="ratecard-zone-tabs">
            {[["blue", "Blue Zone", "text-blue-600 border-blue-600"], ["green", "Green Zone", "text-emerald-600 border-emerald-600"]].map(([k, lbl, activeCls]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`ratecard-tab-${k}`}
                className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors duration-150 ${tab === k ? `${activeCls} bg-white` : "border-transparent text-slate-400 hover:text-slate-700"}`}
              >
                {lbl} ({orgs.filter((o) => inTab(o, k)).length})
                {orgs.filter((o) => inTab(o, k) && !o.project_id).length > 0 && (
                  <span className="ml-1.5 text-[9px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5">{orgs.filter((o) => inTab(o, k) && !o.project_id).length}</span>
                )}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma ara..." data-testid="ratecard-search" className="rounded-none h-9 text-xs border-slate-200 sm:w-[260px]" />
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
              <Checkbox checked={onlyUnmapped} onCheckedChange={(c) => setOnlyUnmapped(!!c)} className="rounded-none" data-testid="ratecard-only-unmapped" />
              Sadece eşleşmemişler ({unmappedCount})
            </label>
            <span className="text-[11px] text-slate-400 ml-auto">{tabOrgs.length} firma · {tabOrgs.length - unmappedCount} eşleşti</span>
          </div>
          <div className="mt-3 border border-slate-200 divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
            {list.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-400">Sonuç yok.</div>}
            {list.map((o) => (
              <div key={o.organisation} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`ratecard-org-${o.organisation}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{o.organisation}</div>
                  <div className="text-[10px] text-slate-400 tabular-nums">{o.orders} sipariş · {Number(o.total).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} {o.currency === "TRY" ? "TL" : o.currency}{o.zone ? ` · ${o.zone}` : ""}</div>
                </div>
                {!o.project_id && <span className="text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 shrink-0">EŞLEŞMEDİ</span>}
                <ProjectCombobox org={o} projects={projects} tabZoneName={tabZoneName} onSelect={(id) => setMap(o, id)} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const TrashRepairCard = () => {
  const [trash, setTrash] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    api.get("/projects-trash").then((r) => setTrash(r.data)).catch(() => {});
    api.get("/projects-orphans").then((r) => setOrphans(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const restoreTrash = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/projects-trash/${p.id}/restore`);
      toast.success(`"${p.name}" geri yüklendi — Projeler listesinde`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const restoreOrphan = async (o) => {
    setBusyId(o.project_id);
    try {
      await api.post(`/projects-orphans/${o.project_id}/restore`, { name: o.title });
      toast.success(`"${o.title}" yeniden oluşturuldu — teklif talepleri otomatik bağlandı`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");

  return (
    <div className="border border-slate-200 bg-white max-w-3xl" data-testid="trash-repair-card">
      <div className="px-6 py-5 border-b border-slate-200">
        <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
          <Trash2 size={18} className="text-red-500" /> Proje Çöp Kutusu & Veri Onarımı
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Silinen projeler artık çöp kutusuna taşınır ve buradan geri yüklenebilir. "Sahipsiz teklif talepleri" bölümü, projesi kalıcı silinmiş teklif taleplerini listeler — "Yeniden Oluştur" ile proje aynı ID'siyle geri gelir ve talepleri otomatik bağlanır.
        </p>
      </div>
      <div className="px-6 py-4 border-b border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Çöp Kutusu ({trash.length})</div>
        {trash.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="trash-empty">Çöp kutusu boş.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {trash.map((p) => (
              <li key={p.id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`trash-row-${p.id}`}>
                <span className="text-sm font-semibold">{p.name}</span>
                <span className="text-xs text-slate-400 flex-1">{p.zone || "—"}{p.stand_no ? ` · ${p.stand_no}` : ""}{p.request_count ? ` · ${p.request_count} teklif talebi` : ""}</span>
                <span className="text-[11px] text-slate-400">{fmtD(p.deleted_at)} · {p.deleted_by}</span>
                <Button variant="outline" size="sm" onClick={() => restoreTrash(p)} disabled={busyId === p.id} data-testid={`trash-restore-${p.id}`} className="rounded-none h-8 gap-1.5 text-xs border-slate-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors duration-200">
                  <RotateCcw size={12} /> Geri Yükle
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="px-6 py-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Sahipsiz Teklif Talepleri ({orphans.length})</div>
        {orphans.length === 0 ? (
          <div className="text-xs text-slate-400" data-testid="orphans-empty">Sahipsiz teklif talebi yok — tüm talepler bir projeye bağlı.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {orphans.map((o) => (
              <li key={o.project_id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`orphan-row-${o.project_id}`}>
                <span className="text-sm font-semibold">{o.title}</span>
                <span className="text-xs text-slate-400 flex-1">{o.request_count} teklif talebi · son: {fmtD(o.last_request_at)}</span>
                <Button variant="outline" size="sm" onClick={() => restoreOrphan(o)} disabled={busyId === o.project_id} data-testid={`orphan-restore-${o.project_id}`} className="rounded-none h-8 gap-1.5 text-xs border-slate-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors duration-200">
                  <RotateCcw size={12} /> Yeniden Oluştur
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const [questions, setQuestions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newQ, setNewQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const load = () => api.get("/quick-questions").then((r) => { setQuestions(r.data); setLoaded(true); }).catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newQ.trim()) return;
    setAdding(true);
    try {
      await api.post("/quick-questions", { text: newQ });
      setNewQ("");
      toast.success("Hızlı soru eklendi");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAdding(false);
    }
  };

  const saveEdit = async (q) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/quick-questions/${q.id}`, { text: editText });
      setEditing(null);
      toast.success("Soru güncellendi");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (q) => {
    try {
      await api.delete(`/quick-questions/${q.id}`);
      toast.success("Soru silindi");
      setQuestions((qs) => qs.filter((x) => x.id !== q.id));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Yönetim</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Ayarlar</h1>
      </div>

      <ApproversCard />
      <RatecardMapCard />
      <HubsCard />
      <SaleStatusesCard />
      <ManufacturerCard />
      <IntegrationCard />

      <TemplateCard />

      <TemplateCard
        endpoint="/settings/target-price-template"
        title="Hedef Fiyat Mesaj Şablonu"
        desc="Tek tedarikçili ihalede hedef fiyat verildiğinde tedarikçiye giden e-posta mesajı."
        tid="target-template"
        placeholders={["firma", "ihale", "tutar", "para_birimi"]}
      />

      <TemplateCard
        endpoint="/settings/manual-offer-template"
        title="Manuel Tutar (Komisyon) Mesaj Şablonu"
        desc="Tedarikçi adına fiyat girilip onay e-postası gönderildiğinde kullanılan mesaj."
        tid="manual-template"
        placeholders={["firma", "ihale", "tutar", "para_birimi"]}
      />

      <TemplateCard
        endpoint="/settings/rejection-template"
        title="Red Mesajı Şablonu"
        desc="Kazanan belirlendiğinde kaybeden tedarikçilere giden nazik red e-postasının gövde metni."
        tid="rejection-template"
        placeholders={["yetkili", "firma", "ihale"]}
      />

      <TemplateCard
        endpoint="/settings/approval-template"
        title="Teklif Onayı E-posta Şablonu"
        desc="Teklifler yönetici onayına gönderilirken onaylayıcıya giden e-postanın gövde metni."
        tid="approval-template"
        placeholders={["yetkili", "ihale", "teklif_sayisi"]}
      />

      <div className="border border-slate-200 bg-white max-w-3xl">
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
            <MessageCircleQuestion size={18} className="text-blue-600" /> Hızlı Sorular
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Teklif karşılaştırma sayfasındaki "Soru Sor" penceresinde listelenen hazır sorular. Tedarikçiye tek tıkla gönderilir.
          </p>
        </div>
        <div className="px-6 py-4 border-b border-slate-200 flex gap-2">
          <Input
            value={newQ}
            onChange={(e) => setNewQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Yeni hızlı soru yazın..."
            data-testid="new-question-input"
            className="rounded-none border-slate-200 h-11"
          />
          <Button onClick={add} disabled={adding || !newQ.trim()} data-testid="add-question-button" className="rounded-none h-11 px-5 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
            <Plus size={15} /> Ekle
          </Button>
        </div>
        {questions.length === 0 ? (
          <div className="px-6 py-10 text-center text-slate-500 text-sm" data-testid="questions-empty">
            {loaded ? "Henüz hızlı soru yok." : "Yükleniyor..."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {questions.map((q) => (
              <li key={q.id} className="px-6 py-3.5 flex items-center gap-3" data-testid={`question-row-${q.id}`}>
                {editing === q.id ? (
                  <>
                    <Input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(q); }} data-testid={`edit-question-input-${q.id}`} className="rounded-none border-slate-200 h-9 flex-1" autoFocus />
                    <button onClick={() => saveEdit(q)} data-testid={`save-question-${q.id}`} title="Kaydet" className="text-emerald-600 hover:text-emerald-500 transition-colors duration-200"><Check size={16} /></button>
                    <button onClick={() => setEditing(null)} data-testid={`cancel-edit-${q.id}`} title="Vazgeç" className="text-slate-400 hover:text-slate-600 transition-colors duration-200"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{q.text}</span>
                    <button onClick={() => { setEditing(q.id); setEditText(q.text); }} data-testid={`edit-question-${q.id}`} title="Düzenle" className="text-slate-400 hover:text-slate-700 transition-colors duration-200"><Pencil size={14} /></button>
                    <button onClick={() => remove(q)} data-testid={`delete-question-${q.id}`} title="Sil" className="text-slate-400 hover:text-red-500 transition-colors duration-200"><Trash2 size={14} /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-slate-200 bg-white max-w-3xl" data-testid="services-card">
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="font-heading font-bold text-lg tracking-tight flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" /> Dahil ve Hariç Hizmetler
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Teklif Al formunda seçilebilen hizmet kalemleri. Seçilenler talepte ve tedarikçiye giden e-postada "Dahil / Hariç Hizmetler" olarak gösterilir.
          </p>
        </div>
        <div className="px-6 py-5 flex flex-col md:flex-row gap-8">
          <ServiceSection kind="dahil" title="Dahil Hizmetler" color="text-emerald-700" icon={CheckCircle2} />
          <ServiceSection kind="haric" title="Hariç Hizmetler" color="text-red-600" icon={XCircle} />
        </div>
      </div>

      <TrashRepairCard />

      <div className="max-w-5xl">
        <ContactsManager base="" projectsEndpoint="/projects" idp="admin-contacts" />
      </div>
    </div>
  );
}
